/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';
import { CloseIcon } from '@coursera/cds-icons';
import type { CoachLocation } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { CoachIconButton } from 'bundles/ai-coach-platform/components/building-blocks';
import SparkleIcon from 'bundles/unified-common/components/coach-on-discovery/SparkleIcon';
import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';

import _t from 'i18n!nls/unified-common';

type CourseraAiHeaderProps = {
  icon?: React.ReactNode;
  title?: string;
  hideTitle?: boolean;
  closeBtnEventLocation?: CoachLocation;
};

const styles = {
  container: css`
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
  brandingContainer: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-50);
  `,
};

const CourseraAiHeader = ({
  icon = <SparkleIcon size="small" />,
  title = _t('Ask Coursera'),
  hideTitle = false,
  closeBtnEventLocation,
}: CourseraAiHeaderProps) => {
  const { setCoachOpen, contextConfig } = useCoachState();
  const track = useTracker();

  const handleClose = () => {
    track('interact_coach', {
      coachInteractionType: 'close_coach_tool',
      coach: {
        mode: contextConfig.config.coachModeId,
        location: closeBtnEventLocation ?? 'toolbar',
      },
    });

    // Coach eventing consolidation [COACH-2404]: dual-emit onto the new ClickCoachElement schema
    track('click_coach_element', {
      clickElementType: 'close_coach_tool',
      coach: {
        mode: contextConfig.config.coachModeId,
        location: closeBtnEventLocation ?? 'toolbar',
      },
    });
    setCoachOpen(false);
  };

  return (
    <div css={styles.container}>
      <div css={styles.brandingContainer}>
        {icon}
        {!hideTitle && (
          <Typography2 component="h2" variant="subtitleSmall">
            {title}
          </Typography2>
        )}
      </div>
      <div>
        <CoachIconButton
          size="small"
          icon={<CloseIcon color="default" />}
          aria-label={_t('Close chat')}
          tooltip={_t('Close chat')}
          tooltipPlacement="bottom"
          onClick={handleClose}
          data-testid="chat-header-close"
        />
      </div>
    </div>
  );
};

export default CourseraAiHeader;
